import { useCallback, useMemo, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";

type Match = {
	text: string;
	start: number;
	end: number;
	severity: "warning" | "block";
	label: string;
	suggestion: string | null;
};

const RULES = [
	{ pattern: /\breviewed\b/gi, severity: "warning" as const, label: "Vague verb", suggestion: "analyzed" },
	{ pattern: /\battended\b/gi, severity: "warning" as const, label: "Vague verb", suggestion: "participated in" },
	{ pattern: /\bvarious\b/gi, severity: "warning" as const, label: "Vague modifier", suggestion: null },
	{ pattern: /\bmisc(?:ellaneous)?\b/gi, severity: "block" as const, label: "Block-listed term", suggestion: null },
	{ pattern: /\bredacted\b/gi, severity: "block" as const, label: "Compliance", suggestion: "prepared for production" },
];

const SAMPLE = "Reviewed and attended hearing; prepared various documents and redacted exhibits for filing.";

function detect(text: string): Match[] {
	const matches: Match[] = [];
	for (const rule of RULES) {
		const re = new RegExp(rule.pattern.source, rule.pattern.flags);
		let m: RegExpExecArray | null;
		while ((m = re.exec(text)) !== null) {
			matches.push({
				text: m[0], start: m.index, end: m.index + m[0].length,
				severity: rule.severity, label: rule.label, suggestion: rule.suggestion,
			});
			if (m[0].length === 0) re.lastIndex++;
		}
	}
	return matches.sort((a, b) => a.start - b.start);
}

export function RestrictedWordsDemo() {
	const [value, setValue] = useState(SAMPLE);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const overlayRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const matches = useMemo(() => detect(value), [value]);
	const warnings = matches.filter(m => m.severity === "warning").length;
	const blocks = matches.filter(m => m.severity === "block").length;

	const [tooltip, setTooltip] = useState<{
		severity: "warning" | "block";
		message: string;
		suggestion: string | null;
		x: number;
		y: number;
	} | null>(null);

	const handleScroll = useCallback(() => {
		if (textareaRef.current && overlayRef.current) {
			overlayRef.current.scrollTop = textareaRef.current.scrollTop;
		}
		setTooltip(null);
	}, []);

	const overlay = useMemo(() => {
		if (!matches.length) return null;
		const segments: React.ReactNode[] = [];
		let cursor = 0;
		for (const m of matches) {
			if (m.start > cursor) segments.push(<span key={`t-${cursor}`}>{value.slice(cursor, m.start)}</span>);
			segments.push(
				<mark
					key={`m-${m.start}`}
					data-severity={m.severity}
					data-message={m.label}
					data-suggestion={m.suggestion ?? ""}
					className={cn(
						"text-transparent rounded-sm",
						m.severity === "block"
							? "bg-red-200/70 dark:bg-red-500/25"
							: "bg-amber-200/70 dark:bg-amber-500/25",
					)}
				>
					{value.slice(m.start, m.end)}
				</mark>,
			);
			cursor = m.end;
		}
		if (cursor < value.length) segments.push(<span key={`t-${cursor}`}>{value.slice(cursor)}</span>);
		return segments;
	}, [value, matches]);

	const handleMouseMove = useCallback((e: React.MouseEvent) => {
		if (!overlayRef.current || !containerRef.current) return;
		const marks = overlayRef.current.querySelectorAll<HTMLElement>("mark[data-severity]");
		const containerRect = containerRef.current.getBoundingClientRect();

		for (const mark of marks) {
			const r = mark.getBoundingClientRect();
			if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
				setTooltip({
					severity: mark.dataset.severity as "warning" | "block",
					message: mark.dataset.message ?? "",
					suggestion: mark.dataset.suggestion || null,
					x: r.left - containerRect.left + r.width / 2,
					y: r.top - containerRect.top,
				});
				return;
			}
		}
		setTooltip(null);
	}, []);

	return (
		<div className="not-prose flex flex-col gap-3 rounded-lg border bg-muted/30 p-5">
			<div ref={containerRef} className="relative">
				{overlay && (
					<div
						ref={overlayRef}
						aria-hidden
						className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-sm text-transparent"
						style={{ overflowY: "auto" }}
					>
						{overlay}
					</div>
				)}

				<textarea
					ref={textareaRef}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onScroll={handleScroll}
					onMouseMove={handleMouseMove}
					onMouseLeave={() => setTooltip(null)}
					rows={3}
					placeholder="Type a narrative to see restricted word highlighting..."
					className="relative flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				/>

				{tooltip && (() => {
					const containerWidth = containerRef.current?.offsetWidth ?? 300;
					const tooltipHalfWidth = 100;
					const padding = 8;
					const clampedX = Math.max(
						tooltipHalfWidth + padding,
						Math.min(tooltip.x, containerWidth - tooltipHalfWidth - padding),
					);
					const flipBelow = tooltip.y < 40;

					return (
						<div
							className="pointer-events-none absolute z-50 min-w-[200px] max-w-xs rounded-md border border-border bg-popover px-3 py-2 shadow-md"
							style={{
								left: clampedX,
								top: flipBelow ? tooltip.y + 24 : tooltip.y - 6,
								transform: flipBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
							}}
						>
							<div className="flex items-center gap-1.5 mb-1">
								<span
									className={cn(
										"inline-block h-2 w-2 rounded-full shrink-0",
										tooltip.severity === "block" ? "bg-red-500" : "bg-amber-500",
									)}
								/>
								<span
									className={cn(
										"text-[10px] font-medium uppercase tracking-wide",
										tooltip.severity === "block"
											? "text-red-600 dark:text-red-400"
											: "text-amber-600 dark:text-amber-400",
									)}
								>
									{tooltip.severity === "block" ? "Blocked" : "Warning"}
								</span>
							</div>
							<p className="text-xs font-medium text-foreground">{tooltip.message}</p>
							<p className="text-xs text-muted-foreground">
								{tooltip.suggestion ? (
									<>Suggested: <span className="font-medium text-foreground">{tooltip.suggestion}</span></>
								) : (
									"No replacement suggested"
								)}
							</p>
						</div>
					);
				})()}
			</div>

			<div className="flex items-center justify-between">
				<div className="flex gap-3">
					{warnings > 0 && (
						<span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
							<span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
							{warnings} warning{warnings !== 1 && "s"}
						</span>
					)}
					{blocks > 0 && (
						<span className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
							<span className="inline-block h-2 w-2 rounded-full bg-red-500" />
							{blocks} blocked
						</span>
					)}
					{matches.length === 0 && (
						<span className="text-xs text-muted-foreground">No restricted words found</span>
					)}
				</div>
				<p className="text-[11px] text-muted-foreground">Hover highlighted words for details</p>
			</div>
		</div>
	);
}
